/**
 * Pilotage Planner — the plan document model.
 *
 * A pilotage plan is one self-contained, serialisable bundle of chart-native
 * elements: a route (with a single base speed), wheel-over bearings, transits,
 * clearing bearings and back bearings, depth-contour interactions (follow-as-route
 * sections and clearing-depth lines), freehand annotations, and sticky notes.
 * Everything is stored as plain [lon, lat] geometry so a whole plan saves, loads,
 * duplicates, thumbnails and (later) shares as one lump, independent of any live
 * store's ids.
 *
 * This module is pure: types plus small helpers (make an empty plan, duplicate
 * one with fresh ids, work out its bounds for a thumbnail, count its elements).
 * The store persists it; the map renders it; the toolbar edits it.
 */

/** [lon, lat] WGS84, matching GeoJSON. */
export type LonLat = [number, number];

export interface PlanWaypoint {
  id: string;
  position: LonLat;
  name?: string;
}

export interface PlanRoute {
  waypoints: PlanWaypoint[];
  /** One base speed for the whole plan, knots (drives the per-leg time-to-go). */
  baseSpeedKn: number;
}

/** A wheel-over: at a route waypoint, the object's bearing that triggers the turn. */
export interface WheelOverBearing {
  id: string;
  /** The route waypoint this turn is taken at. */
  waypointId: string;
  /** The landmark / charted object we take the bearing on. */
  target: LonLat;
  targetLabel?: string;
  /** The object's bearing FROM the waypoint, °T (the real-world turn trigger). */
  bearingDeg: number;
}

/** A transit: two charted objects kept in line. */
export interface PlanTransit {
  id: string;
  front: LonLat;
  frontLabel?: string;
  rear: LonLat;
  rearLabel?: string;
  name?: string;
}

/** A clearing bearing: a safety boundary on a charted object. */
export interface PlanClearingBearing {
  id: string;
  anchor: LonLat;
  anchorLabel?: string;
  /** The object's compass bearing FROM the vessel, °T. */
  bearingDeg: number;
  /** Drawn length from the anchor to the free end, nm. */
  lengthNm: number;
  /** Which side (looking along the bearing) is the safe side. */
  safeSide: 'left' | 'right';
  name?: string;
}

/** A heading / back bearing: a single bearing line on one object. */
export interface PlanBackBearing {
  id: string;
  anchor: LonLat;
  anchorLabel?: string;
  bearingDeg: number;
  lengthNm: number;
  /** A leading (heading-to) bearing, or a back (astern) bearing. */
  kind: 'heading' | 'back';
  name?: string;
}

/** A stretch where the route follows a depth contour between two points. */
export interface PlanContourFollow {
  id: string;
  /** The contour's depth value (m) if known. */
  depthM?: number;
  /** The snapped contour path the route follows, [lon, lat] points. */
  path: LonLat[];
}

/** A do-not-cross depth contour; the shallow side is shaded light red. */
export interface PlanClearingDepth {
  id: string;
  depthM?: number;
  /** The contour path, [lon, lat] points. */
  path: LonLat[];
  /** Which side of the path is the shallower (do-not-cross) side. */
  shallowSide: 'left' | 'right';
  name?: string;
}

export type FreehandKind = 'line' | 'arrow' | 'circle';

/** A finger-drawn annotation. For 'circle', points are [centre, edge]. */
export interface PlanFreehand {
  id: string;
  kind: FreehandKind;
  points: LonLat[];
}

/** A sticky note pinned to the chart; tap to read the stored text. */
export interface PlanStickyNote {
  id: string;
  position: LonLat;
  text: string;
}

export interface PilotagePlanDoc {
  id: string;
  name: string;
  createdMs: number;
  updatedMs: number;
  route: PlanRoute;
  wheelOvers: WheelOverBearing[];
  transits: PlanTransit[];
  clearingBearings: PlanClearingBearing[];
  backBearings: PlanBackBearing[];
  contourFollows: PlanContourFollow[];
  clearingDepths: PlanClearingDepth[];
  freehand: PlanFreehand[];
  stickyNotes: PlanStickyNote[];
  /** Cached chart-area bounds for the thumbnail: [minLon, minLat, maxLon, maxLat]. */
  bounds?: [number, number, number, number] | null;
}

/** The default base speed for a fresh plan, knots. */
export const DEFAULT_PLAN_SPEED_KN = 6;

/** A fresh, empty plan. */
export function emptyPlan(id: string, name: string, nowMs: number): PilotagePlanDoc {
  return {
    id,
    name,
    createdMs: nowMs,
    updatedMs: nowMs,
    route: { waypoints: [], baseSpeedKn: DEFAULT_PLAN_SPEED_KN },
    wheelOvers: [],
    transits: [],
    clearingBearings: [],
    backBearings: [],
    contourFollows: [],
    clearingDepths: [],
    freehand: [],
    stickyNotes: [],
    bounds: null,
  };
}

/** Every [lon, lat] point referenced anywhere in a plan (for bounds/thumbnails). */
export function planPoints(doc: PilotagePlanDoc): LonLat[] {
  const pts: LonLat[] = [];
  for (const w of doc.route.waypoints) pts.push(w.position);
  for (const w of doc.wheelOvers) pts.push(w.target);
  for (const t of doc.transits) pts.push(t.front, t.rear);
  for (const c of doc.clearingBearings) pts.push(c.anchor);
  for (const b of doc.backBearings) pts.push(b.anchor);
  for (const cf of doc.contourFollows) pts.push(...cf.path);
  for (const cd of doc.clearingDepths) pts.push(...cd.path);
  for (const f of doc.freehand) pts.push(...f.points);
  for (const n of doc.stickyNotes) pts.push(n.position);
  return pts;
}

/** Bounding box of everything in the plan, or null if it's empty. */
export function planBounds(doc: PilotagePlanDoc): [number, number, number, number] | null {
  const pts = planPoints(doc);
  if (pts.length === 0) return null;
  let minLon = Infinity;
  let minLat = Infinity;
  let maxLon = -Infinity;
  let maxLat = -Infinity;
  for (const [lon, lat] of pts) {
    if (lon < minLon) minLon = lon;
    if (lat < minLat) minLat = lat;
    if (lon > maxLon) maxLon = lon;
    if (lat > maxLat) maxLat = lat;
  }
  return [minLon, minLat, maxLon, maxLat];
}

/** How many editable elements a plan holds (for the list "3 legs · 2 transits…"). */
export function planElementCount(doc: PilotagePlanDoc): number {
  return (
    doc.route.waypoints.length +
    doc.wheelOvers.length +
    doc.transits.length +
    doc.clearingBearings.length +
    doc.backBearings.length +
    doc.contourFollows.length +
    doc.clearingDepths.length +
    doc.freehand.length +
    doc.stickyNotes.length
  );
}

export function planIsEmpty(doc: PilotagePlanDoc): boolean {
  return planElementCount(doc) === 0;
}

/**
 * Duplicate a plan with fresh ids throughout, so the copy can sit alongside the
 * original with no id clashes (waypoint ids are also referenced by wheel-overs,
 * so those references are re-pointed to the new ids).
 */
export function duplicatePlan(
  doc: PilotagePlanDoc,
  newId: string,
  nowMs: number,
  makeId: (prefix: string) => string,
  name?: string,
): PilotagePlanDoc {
  const wpIdMap = new Map<string, string>();
  const waypoints = doc.route.waypoints.map((w) => {
    const id = makeId('wp');
    wpIdMap.set(w.id, id);
    return { ...w, id, position: [...w.position] as LonLat };
  });
  return {
    id: newId,
    name: name ?? `${doc.name} copy`,
    createdMs: nowMs,
    updatedMs: nowMs,
    route: { waypoints, baseSpeedKn: doc.route.baseSpeedKn },
    wheelOvers: doc.wheelOvers.map((w) => ({
      ...w,
      id: makeId('wo'),
      waypointId: wpIdMap.get(w.waypointId) ?? w.waypointId,
      target: [...w.target] as LonLat,
    })),
    transits: doc.transits.map((t) => ({ ...t, id: makeId('tr'), front: [...t.front] as LonLat, rear: [...t.rear] as LonLat })),
    clearingBearings: doc.clearingBearings.map((c) => ({ ...c, id: makeId('cb'), anchor: [...c.anchor] as LonLat })),
    backBearings: doc.backBearings.map((b) => ({ ...b, id: makeId('bb'), anchor: [...b.anchor] as LonLat })),
    contourFollows: doc.contourFollows.map((cf) => ({ ...cf, id: makeId('cf'), path: cf.path.map((p) => [...p] as LonLat) })),
    clearingDepths: doc.clearingDepths.map((cd) => ({ ...cd, id: makeId('cd'), path: cd.path.map((p) => [...p] as LonLat) })),
    freehand: doc.freehand.map((f) => ({ ...f, id: makeId('fh'), points: f.points.map((p) => [...p] as LonLat) })),
    stickyNotes: doc.stickyNotes.map((n) => ({ ...n, id: makeId('sn'), position: [...n.position] as LonLat })),
    bounds: doc.bounds ? [...doc.bounds] : null,
  };
}
